import React, { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import ConfigurationProvider from './ConfigurationProvider';
import FlagsProvider from './FlagsProvider';
import ImagesProvider from './ImagesProvider';
import PriceProvider from './PriceProvider';
import useContext from '../../../../hooks/useContext';
import { SiteContext } from '../../../../SiteContext';
import useParseProduct, { IUIProduct } from './useParseProduct';
import { useTrackProductView } from '../../../../hooks/tracking/useTrackingEvents';
import { GridView } from '../../CategoryPage/Providers/ViewSettingsProvider';

interface IProductContext extends IUIProduct {
  configurationId?: string;
  groupId: string;
  m3Id: string;
  variantId: string;
  productView: GridView;
  closestVariantUrl?: string;

  setConfigurationId: (configurationId: string) => void;
  setVariantId: (variantId: string) => void;
  setGroupId: (groupId: string) => void;
  setM3Id: (m3Id: string) => void;
  updateParsedProduct: (variant: IOptionResponse) => void;
  setProductView: (value: GridView) => void;
}

export const ProductContext = createContext<IProductContext | undefined>(undefined);

interface IProductProvider {
  children: ReactNode;
  configuration?: ConfigSelection;
  configurationId?: string;
  product: IArticle;
  closestVariantUrl?: string;
}

const ProductProvider = ({
  children,
  configuration,
  configurationId: initialConfigurationId,
  product,
  closestVariantUrl,
}: IProductProvider) => {
  const { data } = useContext(SiteContext);

  const { groupId: initialGroupId, id: initialVariantId, m3Id: initialM3Id } = product;

  const [configurationId, setConfigurationId] = useState(initialConfigurationId);
  const [groupId, setGroupId] = useState(initialGroupId);
  const [m3Id, setM3Id] = useState(initialM3Id);
  const [variantId, setVariantId] = useState(initialVariantId);
  const { parsedProduct, updateParsedProduct } = useParseProduct(product, data.cdnUrl);

  const updatedProduct = {
    ...product,
    id: variantId,
    m3Id: m3Id,
    price: parsedProduct.price,
    flags: parsedProduct.flags,
    specifications: parsedProduct.specifications,
  };

  useTrackProductView(updatedProduct);

  useEffect(() => {
    setConfigurationId(configurationId);
    setGroupId(initialGroupId);
    setM3Id(initialM3Id);
    setVariantId(initialVariantId);
  }, [initialConfigurationId, initialGroupId, initialM3Id, initialVariantId, product]);

  const context = useMemo(
    () =>
      ({
        configurationId,
        groupId,
        m3Id,
        variantId,
        closestVariantUrl,

        setConfigurationId,
        setM3Id,
        setVariantId,
        setGroupId,
        updateParsedProduct,
        ...parsedProduct,
      }) as IProductContext,
    [
      configurationId,
      groupId,
      m3Id,
      updateParsedProduct,
      parsedProduct,
      variantId,
      closestVariantUrl,
    ],
  );

  return (
    <ProductContext.Provider value={context}>
      <PriceProvider {...parsedProduct.price}>
        <FlagsProvider flags={parsedProduct.flags}>
          <ImagesProvider>
            <ConfigurationProvider configuration={configuration}>{children}</ConfigurationProvider>
          </ImagesProvider>
        </FlagsProvider>
      </PriceProvider>
    </ProductContext.Provider>
  );
};

export default ProductProvider;
